import React, { useState } from 'react';
import { FlashFonicProvider, useFlashFonic } from '../../contexts/FlashFonicContext.js';
import FlashClinicalPanel from './FlashClinicalPanel.jsx';
import FonicLiveCapture from '../FonicLiveCapture.jsx';
import UploadControls from '../UploadControls.jsx';
import ClinicalImageCapture from './ClinicalImageCapture.jsx';
import { authApiIdempotent } from '../../firebase/config.js';
import { ENV } from '../../config/env.js';
import { CLINICAL_TIER_LIMITS } from './clinicalOptions.js';

const API_BASE_URL = ENV.apiBaseUrl;

const ClinicalListeningIndicator = () => {
    const ff = useFlashFonic();
    if (!ff?.isListening) return null;
    return (
        <div className="fixed bottom-6 right-6 z-50 inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-200 px-3.5 py-2 ring-1 ring-emerald-400/30 shadow-lg">
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400 animate-pulse" />
            Listening
        </div>
    );
};

const FlashClinicalWorkspace = ({
    isDevMode,
    isOnline = true,
    isSafari,
    homeUsage,
    entitlements,
    remaining,
    clinicalInputType,
    onClinicalInputTypeChange,
    clinicalSettings,
    onClinicalSettingsChange,
    clinicalNotes,
    onClinicalNotesChange,
    onClinicalBatchGenerate,
    onClinicalNotesPaste,
    clinicalPastedImagePreviewUrl,
    onRemoveClinicalPastedImage,
    isClinicalGenerating,
    isGenerating,
    generateClinicalFlashcardRequest,
    flashFonicNotify,
    upload,
    clinicalSettingsForPayload,
    onOpenSettings,
    targetFolderId,
    onTargetFolderChange,
    folders,
    onCreateFolder,
    isMovingQueueCards,
    imagePreview,
    documentFileName,
    dragActive,
    isCameraOn,
    cameraErr,
    fileInputRef,
    videoRef,
    canvasRef,
    onDrop,
    onDragEnter,
    onDragLeave,
    onFileChange,
    openFilePicker,
    startCamera,
    stopCamera,
    takePicture,
    statusMsg,
    onImageCropReady,
    onSelectionError,
    generatedCards,
    renderFoldersSection,
}) => {
    const [clinicalUploadDuration, setClinicalUploadDuration] = useState(15);
    const [isClinicalUploadGenerating, setIsClinicalUploadGenerating] = useState(false);

    const buildClinicalPayload = () => ({
        examType: clinicalSettingsForPayload.examType,
        cardStyle: clinicalSettingsForPayload.cardStyle,
    });

    const handleClinicalProcessAudio = async () => {
        if (!upload.uploadedFile) return;
        upload.setIsProcessing(true);
        flashFonicNotify('Uploading and processing audio...');
        try {
            const reader = new FileReader();
            reader.readAsDataURL(upload.uploadedFile);
            reader.onloadend = async () => {
                const base64File = (reader.result || '').toString().split(',')[1];
                try {
                    const data = await authApiIdempotent(`${API_BASE_URL}/process-audio`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            audio_data: base64File,
                            mime_type: (upload.uploadedFile && upload.uploadedFile.type) || '',
                        }),
                    });
                    upload.setAudioCacheId(data.audioId);
                    flashFonicNotify('Audio is ready! You can now flash it!');
                } catch (e) {
                    flashFonicNotify(e.message || 'Process failed');
                } finally {
                    upload.setIsProcessing(false);
                }
            };
        } catch (_) {
            upload.setIsProcessing(false);
        }
    };

    const handleClinicalUploadFlash = async () => {
        try {
            if (!isDevMode && Number(entitlements?.creditsBalance ?? 0) <= 0) {
                flashFonicNotify('Not enough credits. Upgrade to keep flashing!');
                return;
            }
            setIsClinicalUploadGenerating(true);
            const activePlayer =
                upload.fileType === 'video' ? upload.videoPlayerRef.current : upload.audioPlayerRef.current;
            const savedPlayback = activePlayer
                ? (await import('../../lib/globalAudio.js')).default.captureState()
                : { wasPlaying: false, currentTime: 0 };
            try {
                (await import('../../lib/globalAudio.js')).default.restoreState(savedPlayback);
            } catch (_) {}
            const requestBody = {
                startTime: activePlayer?.currentTime || 0,
                duration: clinicalUploadDuration,
                is_live_capture: false,
                clinical: buildClinicalPayload(),
            };
            if (upload.audioCacheId) {
                requestBody.audioId = upload.audioCacheId;
            } else if (upload.uploadedFile) {
                const reader = new FileReader();
                reader.readAsDataURL(upload.uploadedFile);
                await new Promise((resolve) => {
                    reader.onloadend = () => {
                        requestBody.audio_data = (reader.result || '').toString().split(',')[1];
                        try {
                            requestBody.mime_type = upload.uploadedFile.type || '';
                        } catch (_) {}
                        resolve();
                    };
                });
            } else {
                return;
            }
            await generateClinicalFlashcardRequest(requestBody);
            if (activePlayer) {
                const ga = (await import('../../lib/globalAudio.js')).default;
                ga.restoreState(savedPlayback);
            }
        } finally {
            setIsClinicalUploadGenerating(false);
        }
    };

    const tierLimits = CLINICAL_TIER_LIMITS[entitlements?.tier] || CLINICAL_TIER_LIMITS.free;
    const maxCards = tierLimits.maxCards; // backend clamps to the same 15 regardless of dev mode

    const creditsDisplay = isDevMode
        ? '∞'
        : String(typeof entitlements?.creditsBalance === 'number' ? entitlements.creditsBalance : remaining);

    const canBatchGenerate =
        isOnline &&
        (clinicalInputType === 'notes'
            ? clinicalNotes.trim().length > 0
            : clinicalInputType === 'image'
              ? !!(imagePreview || documentFileName)
              : false);

    return (
        <>
            <FlashFonicProvider
                isDevMode={isDevMode}
                usage={homeUsage}
                duration={Number(localStorage.getItem('ff:listenMinutes') || '0')}
                isSafari={isSafari}
                setNotification={flashFonicNotify}
                generateFlashcardRequest={generateClinicalFlashcardRequest}
            >
                <FlashClinicalPanel
                    inputType={clinicalInputType}
                    onInputTypeChange={onClinicalInputTypeChange}
                    settings={clinicalSettings}
                    onSettingsChange={onClinicalSettingsChange}
                    notesText={clinicalNotes}
                    onNotesTextChange={onClinicalNotesChange}
                    onNotesPaste={onClinicalNotesPaste}
                    pastedImagePreviewUrl={clinicalPastedImagePreviewUrl}
                    onRemovePastedImage={onRemoveClinicalPastedImage}
                    onGenerateFromNotes={onClinicalBatchGenerate}
                    isGenerating={isClinicalGenerating || isGenerating}
                    canGenerate={canBatchGenerate}
                    creditsDisplay={creditsDisplay}
                    creditsHint="1 credit = 1 question or flashcard generated"
                    maxCards={maxCards}
                    entitlementsTier={entitlements?.tier || 'free'}
                    onOpenSettings={onOpenSettings}
                    liveCapture={
                        <FonicLiveCapture
                            targetFolderId={targetFolderId}
                            onTargetFolderChange={onTargetFolderChange}
                            folders={folders}
                            onCreateFolder={onCreateFolder}
                            isMovingQueueCards={isMovingQueueCards}
                        />
                    }
                    uploadCapture={
                        <UploadControls
                            isDevMode={isDevMode}
                            entitlements={entitlements}
                            fileName={upload.fileName}
                            triggerFileUpload={upload.triggerFileUpload}
                            fileInputRef={upload.fileInputRef}
                            handleFileChange={upload.handleFileChange}
                            mediaSrc={upload.mediaSrc}
                            handleCancelUpload={upload.handleCancelUpload}
                            fileType={upload.fileType}
                            videoPlayerRef={upload.videoPlayerRef}
                            audioPlayerRef={upload.audioPlayerRef}
                            togglePlayPause={upload.togglePlayPause}
                            isPlaying={upload.isPlaying}
                            handleSeek={upload.handleSeek}
                            currentTime={upload.currentTime}
                            mediaDuration={upload.mediaDuration}
                            isProcessing={upload.isProcessing}
                            handleProcessAudio={handleClinicalProcessAudio}
                            audioCacheId={upload.audioCacheId}
                            isUploadAutoFlashOn={upload.isUploadAutoFlashOn}
                            setIsUploadAutoFlashOn={upload.setIsUploadAutoFlashOn}
                            uploadAutoFlashInterval={upload.uploadAutoFlashInterval}
                            setUploadAutoFlashInterval={upload.setUploadAutoFlashInterval}
                            duration={clinicalUploadDuration}
                            setDuration={setClinicalUploadDuration}
                            isGenerating={isClinicalUploadGenerating}
                            handleUploadFlash={handleClinicalUploadFlash}
                            rebuildPreview={upload.rebuildPreview}
                        />
                    }
                    generatedCards={generatedCards}
                    imageCapture={
                        <ClinicalImageCapture
                            imagePreview={imagePreview}
                            documentFileName={documentFileName}
                            dragActive={dragActive}
                            isGenerating={isGenerating || isClinicalGenerating}
                            isCameraOn={isCameraOn}
                            cameraErr={cameraErr}
                            fileInputRef={fileInputRef}
                            videoRef={videoRef}
                            canvasRef={canvasRef}
                            onDrop={onDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onDragEnter={onDragEnter}
                            onDragLeave={onDragLeave}
                            onFileChange={onFileChange}
                            openFilePicker={openFilePicker}
                            startCamera={startCamera}
                            stopCamera={stopCamera}
                            takePicture={takePicture}
                            statusMsg={statusMsg}
                            onImageCropReady={onImageCropReady}
                            onSelectionError={onSelectionError}
                        />
                    }
                />
                <ClinicalListeningIndicator />
            </FlashFonicProvider>
            {renderFoldersSection()}
        </>
    );
};

export default FlashClinicalWorkspace;
